import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import styled from 'styled-components';
import { AnimatedStepBody } from '../animation/StepBodyAnimations';
import { getPostAccountCreationSteps } from '../store/flow/selectors';
import { devices } from 'signup-constants/styles/viewports.styles';
import { GreetingsPretitle } from '../views-components/GreetingsPretitle';
import { StepSubtitle } from '../views-components/StepSubtitle';
import { StepTitle } from '../views-components/StepTitle';
import { Step } from 'signup-constants/Step';
import { LayoutTemplateBelowTitleContainer, LayoutTemplateContentContainer } from '../app/AppViewComponent.styles';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { useIndustry } from '../step-industry/useIndustry';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { getStepConfig } from './getStepConfig';
import { LayoutType } from 'signup-components/types';
import { SidePanelBaseView } from './side-panel/SidePanelBaseView';
import { useIsSU0004Variation } from '../hooks/useIsSU0004Variation';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BaseViewHeader = styled.header.withConfig({
  displayName: "BaseView__BaseViewHeader"
})(["margin-bottom:", ";", " ", " ", " @media ", "{", "}"], ({
  theme
}) => theme.space['600'], props => props.$isSU0004Layout && (props.$stepKey === Step.Review || props.$stepKey === Step.CompanyDomain) && `margin-bottom: ${props.theme.space['800']};`, props => props.$isSU0004Layout && (props.$stepKey === Step.Name || props.$stepKey === Step.Password || props.$stepKey === Step.ManagementLevel || props.$stepKey === Step.CodeVerification || props.$stepKey === Step.PhoneNumber) && `margin-bottom: ${props.theme.space['200']};`, props => !props.$isPostAccountCreationStep && `text-align: center;`, devices.desktop, props => props.$stepKey === Step.HubletSelection && `margin-bottom: 0;`);
const BaseViewHelperText = styled.div.withConfig({
  displayName: "BaseView__BaseViewHelperText"
})(["text-align:left;", " ", ""], props => !props.$isPostAccountCreationStep && `text-align: center;`, props => props.$stepKey === Step.HubletSelection && `text-align: left !important; `);

/**
 * @param hideSidePanelNextButton - Enables the next button in the footer to be hidden on buttons views where the user does not need to click next to get to the next step
 */
export const BaseView = ({
  title,
  helperText,
  children,
  stepKey,
  navMarker,
  preTitleMessage = '',
  addExperimentClassName = false,
  disableAnimation = false,
  nextButtonLabel,
  hideSidePanelNextButton = false
}) => {
  const {
    currentStep
  } = useCurrentStep();
  const step = stepKey || currentStep;
  const {
    layoutType
  } = useLayoutTemplate();
  const isSU0004Variation = useIsSU0004Variation();
  const stepConfig = getStepConfig(step);
  const {
    clickedToSeeAll,
    predictedIndustries
  } = useIndustry();
  const predictions = predictedIndustries.length > 0;
  const postAccountCreation = useSelector(getPostAccountCreationSteps);
  if (!step) {
    return null;
  }
  const shouldRestrictWidth = stepConfig && stepConfig.shouldRestrictWidth;
  const isPostAccountCreationStep = postAccountCreation.includes(currentStep);
  const stepTitle = /*#__PURE__*/_jsx(StepTitle, {
    stepKey: step
  });
  const navMarkerName = navMarker || `${step}_VIEW`;
  const experimentSeleniumAttribute = addExperimentClassName ? {
    'data-selenium-experiment': 'EXPERIMENT'
  } : null;
  const getPretitle = () => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(GreetingsPretitle, {
        hasPreTitle: isValidI18nKey(preTitleMessage)
      }), /*#__PURE__*/_jsx(StepSubtitle, {
        message: preTitleMessage
      })]
    });
  };
  if (layoutType === LayoutType.SidePanel) {
    return /*#__PURE__*/_jsx(SidePanelBaseView, {
      addExperimentClassName: addExperimentClassName,
      disableAnimation: disableAnimation,
      helperText: helperText,
      hideSidePanelNextButton: hideSidePanelNextButton,
      navMarker: navMarker,
      nextButtonLabel: nextButtonLabel,
      stepKey: step,
      title: title,
      children: children
    });
  }
  return /*#__PURE__*/_jsxs(AnimatedStepBody, {
    dataStep: step,
    disableAnimation: disableAnimation,
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: navMarkerName
    }), /*#__PURE__*/_jsxs("div", Object.assign({
      "data-selenium": `${step}_VIEW`
    }, experimentSeleniumAttribute, {
      children: [/*#__PURE__*/_jsxs(BaseViewHeader, {
        $isPostAccountCreationStep: isPostAccountCreationStep,
        $isSU0004Layout: isSU0004Variation,
        $stepKey: step,
        children: [!isSU0004Variation && getPretitle(), title || stepTitle, /*#__PURE__*/_jsx(LayoutTemplateBelowTitleContainer, {
          $isPostAccountCreationStep: isPostAccountCreationStep,
          $isSU0004Layout: isSU0004Variation,
          $shouldRestrictWidth: shouldRestrictWidth,
          $stepKey: step,
          children: /*#__PURE__*/_jsx(BaseViewHelperText, {
            $isPostAccountCreationStep: isPostAccountCreationStep,
            $stepKey: step,
            children: helperText
          })
        })]
      }), /*#__PURE__*/_jsx(LayoutTemplateContentContainer, {
        $clickedToSeeAll: clickedToSeeAll || predictions,
        $isSU0004Layout: isSU0004Variation,
        $layoutType: layoutType,
        $shouldRestrictWidth: shouldRestrictWidth,
        $stepKey: step,
        children: children
      })]
    }))]
  });
};